import { Trash2, Edit2, Columns, BarChart3, Settings, Database, GripVertical } from 'lucide-react';
import ComponentRenderer from '../ComponentRenderer';
import { Button } from '../ui/button';

interface ComponentPreviewProps {
  component: any;
  onEdit?: (component: any) => void;
  onDelete?: (componentId: string) => void;
  onDataEditClick?: (componentId: string) => void;
  onColumnsEditClick?: (componentId: string) => void;
  onChartFieldsEditClick?: (componentId: string) => void;
  onKanbanColumnsEditClick?: (componentId: string) => void;
  onButtonClick?: (componentId: string) => void;
  onComponentChange?: (componentId: string, newConfig: any) => void;
  onAddChild?: (containerId: string, child: any) => void;
  onMoveComponent?: (componentId: string, targetContainerId: string) => void;
  activeFilters?: Record<string, string>;
  onFilterChange?: (field: string, value: string) => void;
  isBuilder?: boolean;
  allComponents?: any[];
  isCompact?: boolean;
  /** Não renderiza a `.drag-handle` de cobertura — usado em filhos de container,
   *  onde essa classe acionaria o arraste do container no grid externo. */
  suppressDragHandle?: boolean;
}

export default function ComponentPreview({
  component,
  onEdit,
  onDelete,
  onDataEditClick,
  onColumnsEditClick,
  onChartFieldsEditClick,
  onKanbanColumnsEditClick,
  onButtonClick,
  onComponentChange,
  onAddChild,
  onMoveComponent,
  activeFilters,
  onFilterChange,
  isBuilder = false,
  allComponents = [],
  isCompact = false,
  suppressDragHandle = false,
}: ComponentPreviewProps) {

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onEdit) {
      onEdit(component);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDelete) {
      onDelete(component.id);
    }
  };

  const handleColumnsEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onColumnsEditClick) {
      onColumnsEditClick(component.id);
    }
  };

  const handleChartFieldsEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onChartFieldsEditClick) {
      onChartFieldsEditClick(component.id);
    }
  };

  const handleDataEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDataEditClick) {
      onDataEditClick(component.id);
    }
  };

  const handleKanbanColumnsEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onKanbanColumnsEditClick) {
      onKanbanColumnsEditClick(component.id);
    }
  };

  const showKanbanColumnsButton = false; // Desabilitado
  const showColumnsButton = component.type === 'table' && component.config.columns?.length > 0;
  const showChartFieldsButton = component.type === 'chart' && 
                                 (component.config.dataSource?.data?.length > 0 || component.config.sourceComponentId);
  const showDataButton = !isBuilder && ['chart', 'list', /*'table'*/, 'relationship-table', 'metric-card'].includes(component.type) && !component.config.sourceComponentId;

  return (
    <div className={isCompact ? "w-auto relative" : "h-full"}>
      <div className={`relative ${isCompact ? "inline-block" : "h-full"} group`}>
        {isBuilder && component.type !== 'container' && !suppressDragHandle && (
          <div
            className="drag-handle absolute cursor-move"
            style={{
              zIndex: 1,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'auto'
            }}
          />
        )}
        {isBuilder && onMoveComponent && component.type !== 'filter' && component.type !== 'container' && (
          <div
            className="absolute top-3 left-3 z-[10000] opacity-0 group-hover:opacity-100 transition-opacity"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('application/x-component-id', component.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            title="Arraste para dentro de um container"
          >
            <div className="p-1.5 bg-secondary rounded shadow-lg cursor-grab active:cursor-grabbing">
              <GripVertical className="w-3.5 h-3.5" />
            </div>
          </div>
        )}
        <ComponentRenderer
          component={component}
          allComponents={allComponents}
          onButtonClick={onButtonClick}
          onComponentChange={onComponentChange}
          activeFilters={activeFilters}
          onFilterChange={onFilterChange}
          isCompact={isCompact}
          isBuilder={isBuilder}
          onEditComponent={onEdit}
          onDeleteComponent={onDelete}
          onDataEditClick={onDataEditClick}
          onColumnsEditClick={onColumnsEditClick}
          onChartFieldsEditClick={onChartFieldsEditClick}
          onKanbanColumnsEditClick={onKanbanColumnsEditClick}
          onAddChild={onAddChild}
          onMoveComponent={onMoveComponent}
        />
        
        {(isBuilder || (onEdit && component.type !== 'filter')) && (
          <div className={`absolute ${isCompact ? 'top-1 right-1' : 'top-3 right-3'} ${isCompact ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity flex gap-1.5`} style={{ zIndex: 10000, pointerEvents: 'none' }}>
            {showKanbanColumnsButton && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleKanbanColumnsEdit}
                className="p-1.5 h-auto shadow-lg"
                title="Editar colunas"
                style={{ pointerEvents: 'auto' }}
              >
                <Settings className="w-3.5 h-3.5" />
              </Button>
            )}
            {showDataButton && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleDataEdit}
                className="p-1.5 h-auto shadow-lg"
                title="Editar dados"
                style={{ pointerEvents: 'auto' }}
              >
                <Database className="w-3.5 h-3.5" />
              </Button>
            )}
            {showColumnsButton && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleColumnsEdit}
                className="p-1.5 h-auto shadow-lg"
                title="Visibilidade das colunas"
                style={{ pointerEvents: 'auto' }}
              >
                <Columns className="w-3.5 h-3.5" />
              </Button>
            )}
            {showChartFieldsButton && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleChartFieldsEdit}
                className="p-1.5 h-auto shadow-lg"
                title="Configurar campos do gráfico"
                style={{ pointerEvents: 'auto' }}
              >
                <BarChart3 className="w-3.5 h-3.5" />
              </Button>
            )}
            {onEdit && (isBuilder || component.type !== 'filter') && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleEdit}
                className={`p-1.5 h-auto shadow-lg ${isCompact ? 'ml-2' : ''}`}
                title={isBuilder ? "Editar" : "Editar configura\u00e7\u00f5es"}
                style={{ pointerEvents: 'auto' }}
              >
                {isBuilder ? (
                  <Edit2 className="w-3.5 h-3.5" />
                ) : (
                  <Settings className="w-3.5 h-3.5" />
                )}
              </Button>
            )}
            {onDelete && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleDelete}
                className="p-1.5 h-auto shadow-lg text-destructive hover:text-destructive"
                title="Excluir"
                style={{ pointerEvents: 'auto' }}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
